import { Column } from '@tanstack/react-table';
import classNames from 'classnames';
import FilterIcon from '@images/svg/filter.svg?react';
import CrossIcon from '@images/svg/close.svg?react';
import RangeIcon from '@images/svg/arrows-h.svg?react';
import DebouncedInput from '@components/primitives/dataGrid/components/DebouncedInput';

export type NumericFilterValue = [string | undefined, string | undefined] | undefined;
export type NewNumericFilterValue =
    | {
          min: number;
          max: number;
          isRangeMode: boolean;
      }
    | undefined;

interface Props<TData> {
    column: Column<TData>;
}

const NumericFilter = <TData extends object>({ column }: Props<TData>) => {
    let columnFilterValue = column.getFilterValue() as NewNumericFilterValue;

    // @ts-expect-error Migrate old filter values to new format
    if (columnFilterValue?.length) {
        const oldColumnFilterValue = column.getFilterValue() as NumericFilterValue;

        if (oldColumnFilterValue) {
            columnFilterValue = {
                min: Number(oldColumnFilterValue[0]),
                max: Number(oldColumnFilterValue[1]),
                isRangeMode: oldColumnFilterValue[0] !== oldColumnFilterValue[1],
            };
        }
    }

    const { min, max, isRangeMode } = columnFilterValue ?? {};
    // const [isRangeMode, setIsRangeMode] = useState<boolean>(min && max ? min !== max : (typeof min === 'string' && !max) || (typeof max === 'string' && !min));
    const showResetButton = columnFilterValue && (!!min || !!max);

    const onReset = () => {
        column.setFilterValue({
            min: undefined,
            max: undefined,
            isRangeMode: columnFilterValue?.isRangeMode || false,
        });
    };

    const toggleIsRangeMode = () => {
        column.setFilterValue({
            ...columnFilterValue,
            isRangeMode: !isRangeMode,
        });
    };

    return (
        <div
            className={classNames('gridFilter', 'gridFilter--numeric', {
                'gridFilter--active': columnFilterValue,
            })}
        >
            <DebouncedInput
                className={classNames('numericGridFilter', {
                    'numericGridFilter--range': isRangeMode,
                })}
                value={min ? min.toString() : ''}
                onChange={(value) => {
                    if (!isRangeMode) {
                        if (!value) {
                            column.setFilterValue(undefined);
                            return;
                        }

                        // If we are not in range mode, we should set both values to the same value
                        column.setFilterValue({
                            ...columnFilterValue,
                            min: Number(value),
                            max: Number(value),
                        });
                        return;
                    }

                    column.setFilterValue((oldValue: NewNumericFilterValue) => {
                        // @ts-expect-error Migrate old filter values to new format
                        if (oldValue?.length) {
                            const oldValueToMigrate = oldValue as unknown as NumericFilterValue;

                            if (!oldValueToMigrate) {
                                return {
                                    min: value,
                                };
                            }

                            return {
                                min: value,
                                max: oldValueToMigrate[1],
                                isRangeMode: value !== oldValueToMigrate[1],
                            };
                        }

                        return {
                            ...oldValue,
                            min: value,
                        };
                    });
                }}
                type="number"
                step="0.01"
                name={`filter-${column.id}-min`}
                data-lpignore="true"
                placeholder={isRangeMode ? 'Min' : ''}
            />
            {isRangeMode ? (
                <DebouncedInput
                    className={classNames('numericGridFilter', {
                        'numericGridFilter--range': isRangeMode,
                    })}
                    value={max ? max.toString() : ''}
                    onChange={(value) =>
                        column.setFilterValue((oldValue: NumericFilterValue) => {
                            return {
                                ...oldValue,
                                max: value,
                            };
                        })
                    }
                    type="number"
                    name={`filter-${column.id}-max`}
                    data-lpignore="true"
                    placeholder="Max"
                />
            ) : null}
            {showResetButton ? (
                <button type="button" tabIndex={-1} className="defaultGridFilter__reset" onClick={onReset}>
                    <CrossIcon />
                </button>
            ) : (
                <button type="button" className="numericGridFilter__toggle" onClick={() => toggleIsRangeMode()}>
                    {isRangeMode ? <RangeIcon className="defaultGridFilter__iconn" /> : <FilterIcon className="defaultGridFilter__iconn" />}
                </button>
            )}
        </div>
    );
};

export default NumericFilter;
